import { Box, Grid, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { createSelectField, Layout } from '@roc/client-portal-shared/components';
import { LoginLogoImg, useBodyBackgroundColor, useBorrowerPortalMediaQueries } from '@roc/feature-app-core';
import { DateField, NumberFormat, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useStore } from '../../../../apps/client-portal-public/src/app/hooks';
import { useEffect, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import { citizenshipStatusOptions, isNil, isNotBlank } from '@roc/feature-utils';
import { format, isValid } from 'date-fns';
import { ArrowBack, ArrowForward, CheckCircle } from '@material-ui/icons';
import { ProgressBar } from './components/progressBar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { VerticalLinearStepper } from './components/verticalLinearStepper';
import { TitleComp } from './components/titleComp';
import { PrimaryAddress } from './components/primaryAddress';

import 'swiper/swiper.min.css'
import 'swiper/modules/pagination/pagination.min.css'
import { useParams } from 'react-router';
import { DecodedParams } from 'libs/client-portal-shared/src/app/modules/creditBackground/stores/requestCreditAuthStore';
import { WAITING_AUTHORIZATION } from '../borrowers/utils/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    containerWrapper: {
      padding: "0 16px",
      marginTop: 0,
    },
    root: {
      width: '100%',
    },
  })
);

function getSteps() {
  return ['Your Information'];
}

const BackgroundStep1_content = observer(({
  disableNameFields = false,
  setNextDisabled,
}: {
  disableNameFields: boolean,
  setNextDisabled?: (val: boolean) => void;
}) => {
  const { publicCreditAuthorizationStore } = useStore();
  const creditAuthorizationStore = publicCreditAuthorizationStore.personalInformationFormStore;

  const formFields = creditAuthorizationStore.form.fields;

  const manageDate = (fieldName, date) => {
    let formattedDate = null;
    if (isValid(date)) {
      formattedDate = format(date, 'MM/dd/yyyy');
    }
    creditAuthorizationStore.onFieldChange(fieldName, formattedDate);
  };

  useEffect(() => {
    setNextDisabled(false)
  }, [])

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Personal Information" />
              <Box mt={2} mb={4} fontSize={18}>
                The following information is needed to begin the process of determining your identity and finding your background report.
              </Box>
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="firstName"
                required
                variant="outlined"
                label="First Name"
                value={formFields.firstName.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'firstName',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.firstName.error)}
                helperText={formFields.firstName.error}
                fullWidth
                disabled={disableNameFields}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="lastName"
                required
                variant="outlined"
                label="Last Name"
                value={formFields.lastName.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'lastName',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.lastName.error)}
                helperText={formFields.lastName.error}
                fullWidth
                disabled={disableNameFields}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateField
                testId="dateOfBirth"
                required
                inputVariant="outlined"
                format="MM/dd/yyyy"
                label="Date of Birth"
                value={formFields.dateOfBirth.value}
                onChange={date => manageDate('dateOfBirth', date)}
                disableFuture={true}
                fullWidth
                error={isNotBlank(formFields.dateOfBirth.error)}
                helperText={formFields.dateOfBirth.error}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {createSelectField({
                store: creditAuthorizationStore,
                testId: 'citizenshipStatus',
                fieldName: 'citizenshipStatus',
                label: 'Citizenship Status',
                options: citizenshipStatusOptions,
                placeholder: 'Make Selection',
                standaloneLabel: false
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                customInput={TextField}
                format="###-##-####"
                mask="_"
                variant="outlined"
                label="SSN"
                testId="ssn"
                value={formFields.ssn.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'ssn',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.ssn.error)}
                helperText={formFields.ssn.error}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Primary Address" />
            </Box>
          </Grid>
          <PrimaryAddress store={creditAuthorizationStore} />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Contact Information" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="emailAddress"
                required
                variant="outlined"
                label="Email Address"
                value={formFields.emailAddress.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'emailAddress',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.emailAddress.error)}
                helperText={formFields.emailAddress.error}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                testId="phoneNumber"
                customInput={TextField}
                format="###-###-####"
                mask="_"
                variant="outlined"
                label="Cell Phone"
                value={formFields.phoneNumber.value}
                fullWidth
                required={true}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'phoneNumber',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.phoneNumber.error)}
                helperText={formFields.phoneNumber.error}
              />
            </Grid>
          </Grid>
        </Grid>

      </Grid>
    </Box>
  )
})

export const BackgroundPull = observer(() => {
  const classes = useStyles();
  const { publicCreditAuthorizationStore, requestCreditAuthStore } = useStore();
  const [active, setActive] = useState(0);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const { isSmallScreen, isLargeScreen } = useBorrowerPortalMediaQueries();
  const steps = getSteps();
  const swiperRef = useRef<any>(null);
  const { encodedParams } = useParams<{ encodedParams: string }>();
  const decodedParamsString = isNotBlank(encodedParams) ? atob(encodedParams) : '{}';
  const decodedParams: DecodedParams = JSON.parse(decodedParamsString);
  const [completed, setCompleted] = useState(false);

  const [invalidLink, setInvalidLink] = useState(false);
  const { requestStatus } = publicCreditAuthorizationStore;

  useBodyBackgroundColor("#fff");

  useEffect(() => {
    if (!isNil(decodedParams.requestToken) && requestStatus && requestStatus !== WAITING_AUTHORIZATION) {
      setInvalidLink(true);
    }
  }, [requestStatus]);

  useEffect(() => {
    setPercentCompleted(active * (100 / steps.length));
  }, [active]);

  const handleSaveFormInformation = () => {
    const formData = publicCreditAuthorizationStore.personalInformationFormStore.getFormValues();
    requestCreditAuthStore.saveFormInformation(formData, decodedParams, false);
  }

  useEffect(() => {
    publicCreditAuthorizationStore.resetStore();
    publicCreditAuthorizationStore.fetchInvalidSSN();
    if (!isNil(decodedParams.requestToken)) {
      publicCreditAuthorizationStore.fetchBorrowerDetailsVerification(decodedParams.requestToken);
    }
  }, []);

  return (
    <Layout maxWidth="lg" bodyContentBoxClass={classes.containerWrapper}>
      <Box mt={isLargeScreen ? 2 : 0}>
        <LoginLogoImg imgStyle={{
          maxWidth: 300
        }} />
      </Box>
      {invalidLink ? (
        <Box mt={4} textAlign={'center'}>
          <Typography variant="h4">Sorry, this link is no longer valid.</Typography>
        </Box>
      ) : (
        <>
          {
            completed ? null :
              <Box mb={4} textAlign={'left'}>
                <Typography variant={isSmallScreen ? 'h4' : 'h3'}>
                  Your Information
                </Typography>
                <Box>
                  <ProgressBar value={percentCompleted} />
                </Box>
              </Box>
          }
          {
            completed ?
              <Grid container spacing={2}>
                <Box mt={3} width={'100%'}>
                  <Box textAlign={'center'} margin={'auto'} maxWidth={1000}>
                    <Box pb={2} fontSize={32} display={'flex'} alignItems={'center'} justifyContent={'center'} flexDirection={isSmallScreen ? 'column' : 'row'}>
                      <CheckCircle style={{
                        color: 'green',
                        fontSize: isSmallScreen ? 60 : 40,
                        marginRight: 8,
                      }} />
                      <div>
                        Your information has been successfully submitted!
                      </div>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              :
              <Grid container spacing={2}>
                <Grid item xs zeroMinWidth>
                  <Swiper
                    spaceBetween={50}
                    slidesPerView={1}
                    onSlideChange={(swiper) => {
                      setActive(swiper.realIndex);
                    }}
                    onSwiper={(swiper) => {
                      (swiperRef.current = swiper)
                    }}
                    allowTouchMove={false} // Prevents user swiping if not needed
                  >
                    {steps.map((StepContent, index) => (
                      <SwiperSlide key={index} style={{
                        padding: '0 2px'
                      }}>
                        {
                          active == 0 && index === 0 && <BackgroundStep1_content setNextDisabled={setNextDisabled} disableNameFields={publicCreditAuthorizationStore.disableNameFields} />
                        }
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <Grid item xs={12} sm={12}>
                    <Box mt={4} display={'flex'} justifyContent={active == 0 ? 'flex-end' : 'space-between'}>
                      {
                        active != 0 &&
                        <Button variant='outlined' color='default' startIcon={<ArrowBack />} size='large' onClick={() => {
                          swiperRef.current?.slidePrev();
                        }} style={{
                          padding: '16px 24px'
                        }}>
                          Back
                        </Button>
                      }
                      <Button variant='contained' color='primary' endIcon={active == steps.length - 1 ? null : <ArrowForward />} size='large' onClick={() => {
                        if (active == steps.length - 1) {
                          if (publicCreditAuthorizationStore.validateStep()) {
                            handleSaveFormInformation();
                            setCompleted(true);
                            setPercentCompleted(100);
                          }
                        } else {
                          handleSaveFormInformation();
                          swiperRef.current?.slideNext();
                        }
                      }} disabled={nextDisabled} style={{
                        padding: '16px 24px'
                      }}>
                        {active == steps.length - 1 ? 'Submit' : 'Next'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {
                  isLargeScreen &&
                  <Grid item style={{ height: '100%', width: '1px', background: '#ddd', margin: '0 24px', padding: 0 }}></Grid>
                }
                {
                  isLargeScreen &&
                  <Grid item style={{ width: 280 }}>
                    <VerticalLinearStepper active={active} steps={getSteps()} />
                  </Grid>
                }
              </Grid>
          }
        </>
      )}
    </Layout>
  );
});